import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, CornerDownLeft } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../ui/dialog";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/I18nProvider";
import { DOCS } from "../../content/docs";
import { FAQ } from "../../content/faq";
import { track } from "../../lib/analytics";

const localIndex = [
  ...DOCS.map((d) => ({ kind: "doc", title: d.title, text: d.body.join(" "), path: `/docs#${d.id}` })),
  ...FAQ.flatMap((c) => c.items.map(([q, a]) => ({ kind: "faq", title: q, text: a, path: `/faq?q=${encodeURIComponent(q)}` }))),
];

function excerpt(text, q) {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  const s = Math.max(0, i - 40);
  return (s > 0 ? "…" : "") + text.slice(s, s + 140) + "…";
}

export function SearchDialog({ open, onOpenChange }) {
  const { t } = useI18n();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [remote, setRemote] = useState([]);
  const [loading, setLoading] = useState(false);
  const [active, setActive] = useState(0);
  const inputRef = useRef(null);
  const valid = q.trim().length >= 2;

  useEffect(() => {
    if (!valid) { setRemote([]); return; }
    setLoading(true);
    const h = setTimeout(() => {
      api.get("/search", { params: { q: q.trim() } }).then(({ data }) => setRemote(data.results)).catch(() => setRemote([])).finally(() => setLoading(false));
      track("search", { length: q.trim().length });
    }, 250);
    return () => clearTimeout(h);
  }, [q, valid]);

  const results = useMemo(() => {
    if (!valid) return [];
    const ql = q.trim().toLowerCase();
    const local = localIndex.filter((x) => (x.title + " " + x.text).toLowerCase().includes(ql)).map((x) => ({ ...x, excerpt: excerpt(x.text, ql) }));
    return [...remote, ...local].slice(0, 20);
  }, [remote, q, valid]);

  useEffect(() => setActive(0), [results.length]);

  const go = (r) => { onOpenChange(false); setQ(""); navigate(r.path); };
  const onKey = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter" && results[active]) go(results[active]);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="search-dialog" className="max-w-2xl rounded-[28px] border-sky-100 p-0">
        <DialogTitle className="sr-only">{t("nav.search")}</DialogTitle>
        <DialogDescription className="sr-only">{t("search.hint")}</DialogDescription>
        <div className="flex items-center gap-3 border-b border-sky-100 py-4 pl-5 pr-14">
          <Search className="h-5 w-5 text-[#237BE8]" aria-hidden />
          <input ref={inputRef} data-testid="search-input" autoFocus value={q} maxLength={80} onChange={(e) => setQ(e.target.value)} onKeyDown={onKey}
            placeholder={t("search.placeholder")} aria-label={t("search.placeholder")} className="flex-1 bg-transparent text-base text-[#17345F] outline-none placeholder:text-[#17345F]/40" />
          {q && <button data-testid="search-clear-btn" onClick={() => { setQ(""); inputRef.current?.focus(); }} aria-label={t("common.clear")} className="rounded-full p-1 hover:bg-sky-50"><X className="h-4 w-4" /></button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-3" aria-live="polite">
          {!valid && <p className="px-3 py-6 text-center text-sm text-[#17345F]/60">{t("search.min")}</p>}
          {valid && !loading && results.length === 0 && <p data-testid="search-empty" className="px-3 py-6 text-center text-sm text-[#17345F]/60">{t("search.none")} “{q}”</p>}
          <ul role="listbox" aria-label={t("nav.search")}>
            {results.map((r, i) => (
              <li key={r.path + i} role="option" aria-selected={i === active}>
                <button data-testid={`search-result-${i}`} onMouseEnter={() => setActive(i)} onClick={() => go(r)}
                  className={`w-full rounded-2xl px-4 py-3 text-left transition-colors ${i === active ? "bg-[#EAF7FF]" : ""}`}>
                  <span className="mr-2 rounded-full bg-[#FFD84A]/50 px-2 py-0.5 text-[11px] font-bold uppercase text-[#17345F]">{t(`search.kinds.${r.kind}`)}</span>
                  <span className="font-bold text-[#17345F]">{r.title}</span>
                  <p className="mt-1 line-clamp-2 text-sm text-[#17345F]/65">{r.excerpt}</p>
                </button>
              </li>
            ))}
          </ul>
        </div>
        <p className="flex items-center gap-1 border-t border-sky-100 px-5 py-2.5 text-xs text-[#17345F]/50"><CornerDownLeft className="h-3 w-3" />{t("search.hint")}</p>
      </DialogContent>
    </Dialog>
  );
}
